import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Lock, ShieldCheck, Sparkles } from "lucide-react";

const PERKS = [
  "CINDR // REBUILD 30 — every day, every workout",
  "Full workout library with form guides & logging",
  "AI CINDR Coach — honest mindset support, any time",
  "AI recipe generator built around your fuel focus",
  "Sunday check-ins, promises, habits, badges — the whole system",
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { day: "numeric", month: "short", year: "numeric" }) : "—");

const StatusCard = ({ m, onCancel, cancelling }) => {
  const label = { active: "Active", pending: "Activating…", cancelled: "Cancelled", suspended: "Payment issue", expired: "Expired" }[m.status] || m.status;
  return (
    <div className="card" style={{ borderColor: m.status === "active" ? "#FF0055" : undefined }} data-testid="membership-status-card">
      <p className="eyebrow" style={{ color: "#FF0055" }}>Your membership</p>
      <p className="font-display font-black text-2xl md:text-3xl mt-2 uppercase" data-testid="membership-status">{label}</p>
      <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
        <div><p className="eyebrow">{m.status === "cancelled" ? "Access until" : "Next billing"}</p><p className="mt-1 text-zinc-200" data-testid="membership-next-billing">{fmtDate(m.next_billing_time)}</p></div>
        <div><p className="eyebrow">Since</p><p className="mt-1 text-zinc-200">{fmtDate(m.started_at)}</p></div>
      </div>
      <p className="text-xs text-zinc-500 mt-4">Subscription {m.subscription_id}{m.payer_email ? ` · ${m.payer_email}` : ""}</p>
      {(m.status === "active" || m.status === "suspended" || m.status === "pending") && (
        <button className="btn-secondary mt-6" onClick={onCancel} disabled={cancelling} data-testid="membership-cancel-btn">
          {cancelling ? "Cancelling…" : "Cancel membership"}
        </button>
      )}
      {m.status === "cancelled" && <p className="text-xs text-zinc-400 mt-4">You keep full access until the date above. Resubscribe any time below.</p>}
    </div>
  );
};

export default function MembershipPage() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [membership, setMembership] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/membership/config"), api.get("/membership")]).then(([c, m]) => {
      setConfig(c.data);
      setMembership(m.data);
    });
  }, []);

  if (!config || !membership) return <div className="p-10 text-zinc-500">Loading…</div>;

  const m = membership.membership;
  const canSubscribe = !membership.is_member || m?.status === "cancelled";

  const onApprove = async (data) => {
    setBusy(true);
    setMsg("");
    try {
      const { data: res } = await api.post("/membership/activate", { subscription_id: data.subscriptionID });
      setMembership({ ...membership, ...res });
      await refresh();
      setMsg("You're in. Welcome to the rebuild.");
      setTimeout(() => navigate("/program"), 1800);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Could not activate. Contact support with your PayPal subscription ID.");
    }
    setBusy(false);
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your CINDR membership? You keep access until the end of the paid period.")) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/membership/cancel");
      setMembership({ ...membership, ...res });
      await refresh();
      setMsg("Membership cancelled. No guilt — the door stays open.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Could not cancel.");
    }
    setBusy(false);
  };

  return (
    <div className="fade-up" data-testid="membership-page">
      {params.get("locked") && !membership.is_member && (
        <div className="card mb-6 flex items-center gap-3" style={{ borderColor: "#FF0055" }} data-testid="membership-locked-banner">
          <Lock size={16} color="#FF0055" />
          <p className="text-sm text-zinc-200">That part of CINDR is for members. Unlock everything below.</p>
        </div>
      )}

      <p className="eyebrow" style={{ color: "#FF0055" }}>CINDR Membership</p>
      <h1 className="font-display font-black text-3xl md:text-5xl leading-[0.95] mt-2">
        BUILD HER.<br /><span style={{ color: "#FF0055" }}>EVERY MONTH.</span>
      </h1>
      <p className="text-zinc-300 mt-4 max-w-xl text-sm md:text-base">
        One membership. The whole system — MOVE, FUEL, MIND, RECOVER. Cancel anytime, no drama.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-10">
        <div className="lg:col-span-3 space-y-6">
          {m && <StatusCard m={m} onCancel={cancel} cancelling={busy} />}
          {user?.role === "admin" && (
            <div className="card" data-testid="membership-admin-note">
              <p className="eyebrow" style={{ color: "#FF0055" }}>Admin</p>
              <p className="text-sm text-zinc-300 mt-2">Your account has full access without a subscription. Plan <span className="text-zinc-100">{config.plan_id}</span> · {config.mode} mode.</p>
            </div>
          )}
          <div className="card">
            <p className="eyebrow">What's inside</p>
            <ul className="mt-4 space-y-3">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-zinc-200">
                  <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5" style={{ background: "#FF0055" }}><Check size={12} strokeWidth={3} color="white" /></span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="card concrete relative overflow-hidden" style={{ borderColor: "#FF0055" }} data-testid="membership-pricing-card">
            <div className="relative z-10">
              <p className="eyebrow" style={{ color: "#FF0055" }}>Monthly</p>
              <p className="font-display font-black text-5xl mt-2" data-testid="membership-price">
                ${config.price}<span className="text-zinc-500 text-base ml-1">{config.currency}/mo</span>
              </p>
              <p className="text-xs text-zinc-400 mt-2">Billed monthly through PayPal. Cancel anytime.</p>

              {canSubscribe ? (
                <div className="mt-6" data-testid="paypal-button-wrap">
                  <PayPalScriptProvider options={{ clientId: config.client_id, vault: true, intent: "subscription", currency: config.currency }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "white", shape: "rect", label: "subscribe", height: 48 }}
                      createSubscription={(_, actions) => actions.subscription.create({ plan_id: config.plan_id })}
                      onApprove={onApprove}
                      onError={(err) => setMsg(`PayPal error: ${err?.message || "try again"}`)}
                      disabled={busy}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : (
                <div className="mt-6 flex items-center gap-2 text-sm" style={{ color: "#FF0055" }} data-testid="membership-active-note">
                  <Sparkles size={16} /> You're a member. Go build.
                </div>
              )}

              {config.mode === "sandbox" && (
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-4" data-testid="membership-sandbox-note">Sandbox mode — test payments only</p>
              )}
              <p className="text-xs text-zinc-500 mt-4 flex items-center gap-1.5"><ShieldCheck size={12} /> Secured by PayPal. We never see your card.</p>
            </div>
          </div>
          {msg && <p className="text-sm mt-4" style={{ color: "#FF0055" }} data-testid="membership-msg">{msg}</p>}
        </div>
      </div>
    </div>
  );
}
